import { Alert, Button } from "@svea/design-system-components-react";
import {
	AriaPopover,
	ButtonGroup,
	FormHelperText,
	formatSsn,
	Grid,
	Heading,
	isOnlyNumeric,
	isRequired,
	type LangKey,
	Markdown,
	P,
	Stack,
	Text,
	TextField,
	texts,
	useText,
	validateMin,
	moneyToViewFormat,
} from "@sveabank/cc-common";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import { Form } from "react-final-form";
import { usePageContext } from "../../../../hooks/context";
import type { ClcCompanySharesForm } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { formatNumericValue, getTotalDistributedShares, parseNumericValue } from "../../utils";
import { AddShareholdersField } from "./add-shareholders-field";

type Props = {
	onSubmit: (data: Partial<ClcCompanySharesForm>) => void;
	onCancel: () => void;
};

const minShareCapital = 25000 as const;

const isDivisibleSharePrice = (
	shareCapital: number | undefined,
	numberOfShares: number | undefined,
) => !!shareCapital && !!numberOfShares && shareCapital / numberOfShares > 0;

const validateSharesForm = (
	values: Partial<ClcCompanySharesForm>,
): Record<typeof FORM_ERROR, LangKey> | undefined => {
	if (getTotalDistributedShares(values.shareholders) !== values.numberOfShares)
		return { [FORM_ERROR]: texts.validation.invalidSharesDistributed };

	return undefined;
};

const ClcShares = ({ onSubmit, onCancel }: Props) => {
	const {
		clcApplicationDetails: {
			companyShares: { value: companySharesValues },
			companyRegistration: { isComplete: isCompanyRegistrationComplete },
			beneficialOwners: { isComplete: isBeneficialOwnershipComplete },
		},
		isPostingClcCompanyShares,
	} = usePageContext(useClcCompanyApplication);

	const [t] = useText();

	// don't render the form before we have the applicant contact info:
	if (!isCompanyRegistrationComplete) return null;

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Form<Partial<ClcCompanySharesForm>>
					onSubmit={onSubmit}
					validate={validateSharesForm}
					mutators={{
						...arrayMutators,
					}}
					render={({ handleSubmit, values, error, submitFailed }) => {
						const totalInitialShares = getTotalDistributedShares(companySharesValues?.shareholders);

						const distributedShares = getTotalDistributedShares(values.shareholders);

						const totalShares = values.numberOfShares || 0;
						const shareCapital = values.shareCapital || 0;

						const hasDistributedAllShares = totalShares > 0 && distributedShares === totalShares;

						return (
							<form onSubmit={handleSubmit} name="companyRegistrationForm">
								<Stack gap={8}>
									<Stack gap={2}>
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.shares.sharesHeading)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => <Markdown>{t(texts.newCompany.shares.helpText1)}</Markdown>}
											/>
										</Stack>
										<P>{t(texts.newCompany.shares.sharesText)}</P>
										<Stack gap={4}>
											<TextField<number | undefined>
												fullWidth
												label={t(texts.newCompany.shares.capital)}
												name="shareCapital"
												parse={parseNumericValue}
												format={formatNumericValue}
												validators={[
													isRequired,
													isOnlyNumeric,
													validateMin(
														minShareCapital,
														t(texts.validation.minimumAmount, minShareCapital.toString()),
													),
												]}
												initialValue={companySharesValues?.shareCapital || 25000}
											/>
											<TextField<number | undefined>
												fullWidth
												label={t(texts.newCompany.shares.numberOfShares)}
												name="numberOfShares"
												parse={parseNumericValue}
												format={formatNumericValue}
												initialValue={totalInitialShares || 500}
												validators={[
													isRequired,
													isOnlyNumeric,
													validateMin(1, t(texts.newCompany.shares.validateminCompanyShares)),
												]}
											/>

											{isDivisibleSharePrice(values.shareCapital, values.numberOfShares) && (
												<Alert kind="information">
													{t(
														texts.newCompany.shares.pricePerShare,
														moneyToViewFormat(shareCapital / totalShares),
													)}
												</Alert>
											)}
										</Stack>
									</Stack>
									<Stack gap={2} align="start">
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.shares.shareholdersHeading)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => <Markdown>{t(texts.newCompany.shares.helpText2)}</Markdown>}
											/>
										</Stack>
										<Stack gap={4}>
											<P>{t(texts.newCompany.shares.infoText)}</P>
											{isBeneficialOwnershipComplete && (
												<Alert kind="warning">{t(texts.newCompany.shares.editWarning)}</Alert>
											)}
											<AddShareholdersField initialValues={companySharesValues?.shareholders} />
										</Stack>
									</Stack>
									<Stack gap={2}>
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.shares.distributeSharesHeading)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => <Markdown>{t(texts.newCompany.shares.helpText3)}</Markdown>}
											/>
										</Stack>
										<Stack gap={4}>
											<Alert kind={hasDistributedAllShares ? "success" : "warning"}>
												{t(
													texts.newCompany.shares.distributeSharesText,
													formatNumericValue(distributedShares),
													formatNumericValue(totalShares),
												)}
											</Alert>
											{values.shareholders?.map((s, i) => (
												<Stack key={s.nationalId} gap={1} align="start" fullSize>
													<Text>{`${s.firstName} ${s.lastName} ${formatSsn(s.nationalId)}`}</Text>
													<TextField<number | undefined>
														initialValue={companySharesValues?.shareholders[i]?.numberOfShares || 0}
														label={t(texts.newCompany.shares.numberOfShares)}
														name={`shareholders[${i}].numberOfShares`}
														parse={parseNumericValue}
														format={formatNumericValue}
														validators={[
															isRequired,
															validateMin(1, t(texts.newCompany.shares.validateMinShares)),
														]}
													/>
												</Stack>
											))}
										</Stack>
									</Stack>
									<ButtonGroup row align="start">
										{error && submitFailed ? <FormHelperText error>{error}</FormHelperText> : null}
										<Button type="button" variant="secondary" onPress={onCancel}>
											{t(texts.common.abort)}
										</Button>
										<Button
											type="submit"
											isPending={isPostingClcCompanyShares}
											data-tracking-action="CLC Application Continue"
											data-tracking-name="Company Shares"
										>
											{t(texts.common.continue)}
										</Button>
									</ButtonGroup>
								</Stack>
							</form>
						);
					}}
				/>
			</Grid.Col>
		</Grid>
	);
};

export default ClcShares;
